import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, Signal, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { ComponentsModule, MessageType } from '../../../../lib';
import { DefaultValuesComponent } from '../default-values/default-values.component';
import { CalculationBaseComponent } from '../calculation-base/calculation-base.component';
import { BuildingService, RoomByRoomService } from '../../services';
import { ApiProject, CalculationBase, CalculationSummary, Level, RoomListItem, SetRoomsEvent } from '../../models';
import { DefaultValues } from '../../models/default-values.interface';
import { LevelRoomsPanelComponent } from '../level-rooms-panel/level-rooms-panel.component';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'ca-rbr-calculator',
    imports: [
        MatButton,
        DecimalPipe,
        TranslateModule,
        ComponentsModule,
        DefaultValuesComponent,
        CalculationBaseComponent,
        LevelRoomsPanelComponent
    ],
    templateUrl: './room-by-room-calculator.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./room-by-room-calculator.component.scss']
})
export class RoomByRoomCalculatorComponent implements OnInit {
    public projectId = input.required<string>();

    @ViewChild('addUpperLevelButton') public addUpperLevelButton!: MatButton;
    @ViewChild('addLowerLevelButton') public addLowerLevelButton!: MatButton;
    public calculationSummary: Signal<CalculationSummary | undefined>;
    public calculationBase: Signal<CalculationBase>;
    public defaultValues: Signal<DefaultValues>;
    public project: Signal<ApiProject | null>;
    public levels: Signal<Level[]>;
    public levelsLoading: Signal<boolean>;
    public loadingError: Signal<boolean>;
    public readonly messageType = MessageType;
    private buildingService = inject(BuildingService);
    private roomByRoomService = inject(RoomByRoomService);

    constructor() {
        effect(() => {
            const id = this.projectId();
            if (id) {
                this.buildingService.loadProject(id);
            }
        });
    }

    ngOnInit(): void {
        this.loadingError = this.buildingService.getError();
        this.levelsLoading = this.buildingService.getLoading();
        this.levels = this.buildingService.getLevels();
        this.project = this.buildingService.getProject();
        this.calculationSummary = this.buildingService.getCalculationSummary();
        this.calculationBase = this.buildingService.getCalculationBase();
        this.defaultValues = this.buildingService.getDefaultValues();

        this.roomByRoomService.getConfiguration$().subscribe(config => {
            console.log('API Configuration:', config);
        });
    }

    public loadProjectLevels(): void {
        this.buildingService.loadProject(this.projectId());
    }

    public addUpperLevel(): void {
        this.addUpperLevelButton?.focus();
        this.buildingService.addUpperLevel();
    }

    public addLowerLevel(): void {
        this.addLowerLevelButton?.focus();
        this.buildingService.addLowerLevel();
    }

    public deleteLevel(levelId: string): void {
        this.buildingService.deleteLevel(levelId);
    }

    public setRooms(event: SetRoomsEvent): void {
        this.buildingService.setRoomsToLevel(event);
    }

    public deleteRoom(roomId: string): void {
        this.buildingService.deleteRoom(roomId);
    }

    public updateProject(project: ApiProject | null): void {
        this.buildingService.updateProject(project);
    }

    public toggleCooling(event: { roomId: string; cooling: boolean }): void {
        this.buildingService.toggleHeatingAndCooling(event.roomId, { cooling: event.cooling });
    }

    public toggleHeating(event: { roomId: string; heating: boolean }): void {
        this.buildingService.toggleHeatingAndCooling(event.roomId, { heating: event.heating });
    }

    public onCalculationBaseConfirm(calculationBase: CalculationBase): void {
        this.buildingService.updateCalculationBase(calculationBase);
    }

    public onDefaultValuesConfirm(defaultValues: DefaultValues): void {
        this.buildingService.updateDefaultValues(defaultValues);
    }
}
